import { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useForm, Controller } from 'react-hook-form';
import {
  IconLoader,
  ButtonPrimary,
  ToggleInput,
  ResponseError,
  responseErrorsResolver,
  getResponseErrors,
  Select,
} from '@ui/src';
import { useAppSelector } from '@store';
import { PSPDetails } from '../../../../../store/types';
import {
  useEditBeneficiarySettingsMutation,
  useLazyGetPSPDetailsQuery,
} from '../settings/store/services/details';
import {
  TransactionsSettingsKeys,
  IbanValidationFormValues,
} from '../settings/types';

import cn from '../settings/styles.module.css';

const providerList = [
  {
    name: 'None',
    value: 'none',
  },
  {
    name: 'iban.com',
    value: 'iban.com',
  },
];

const IbanValidationSettings = () => {
  const [defaultFormValues, setDefaultFormValues] =
    useState<IbanValidationFormValues>();
  const [globalError, setGlobalError] = useState('');
  const [errorList, setErrorList] = useState('');
  const [provider, setProvider] = useState('none');

  const [getIbanSettings, { isLoading, isFetching }] =
    useLazyGetPSPDetailsQuery();
  const [edit, { isLoading: editIsLoading }] =
    useEditBeneficiarySettingsMutation();

  const {
    settings: {
      details: { pspDetails },
    },
  } = useAppSelector((state) => state);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<IbanValidationFormValues>();

  const fields = watch();

  const isDirty = !isEqual(fields, defaultFormValues);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const updatedSettings: {
      [key: string]:
        | string
        | boolean
        | string[]
        | Record<string, string>
        | Record<string, boolean>;
      key: TransactionsSettingsKeys;
    }[] = [];

    // if (provider !== 'none') {
    if (
      values.ibanComIbanValidationAvailableForCustomers !==
      defaultFormValues?.ibanComIbanValidationAvailableForCustomers
    ) {
      updatedSettings.push({
        ibanComIbanValidationAvailableForCustomers:
          values.ibanComIbanValidationAvailableForCustomers as boolean,
        key: TransactionsSettingsKeys.IBAN_COM_IBAN_VALIDATION_AVAILABLE_FOR_CUSTOMERS,
      });
    }

    if (
      values.ibanComBicValidationAvailableForCustomers !==
      defaultFormValues?.ibanComBicValidationAvailableForCustomers
    ) {
      updatedSettings.push({
        ibanComBicValidationAvailableForCustomers:
          values.ibanComBicValidationAvailableForCustomers as boolean,
        key: TransactionsSettingsKeys.IBAN_COM_BIC_VALIDATION_AVAILABLE_FOR_CUSTOMERS,
      });
    }

    if (
      values.enableBicDataFromExternalSourceEdit !==
      defaultFormValues?.enableBicDataFromExternalSourceEdit
    ) {
      updatedSettings.push({
        enableBicDataFromExternalSourceEdit:
          values.enableBicDataFromExternalSourceEdit as boolean,
        key: TransactionsSettingsKeys.ENABLE_BIC_DATA_FROM_EXTERNAL_SOURCE_EDIT,
      });
    }

    if (
      values.enableIbanDataFromExternalSourceEdit !==
      defaultFormValues?.enableIbanDataFromExternalSourceEdit
    ) {
      updatedSettings.push({
        enableIbanDataFromExternalSourceEdit:
          values.enableIbanDataFromExternalSourceEdit as boolean,
        key: TransactionsSettingsKeys.ENABLE_IBAN_DATA_FROM_EXTERNAL_SOURCE_EDIT,
      });
    }

    if (
      values.ibanComIbanValidationAvailableForEmployees !==
      defaultFormValues?.ibanComIbanValidationAvailableForEmployees
    ) {
      updatedSettings.push({
        ibanComIbanValidationAvailableForEmployees:
          values.ibanComIbanValidationAvailableForEmployees as boolean,
        key: TransactionsSettingsKeys.IBAN_COM_IBAN_VALIDATION_AVAILABLE_FOR_EMPLOYEES,
      });
    }

    if (
      values.ibanComBicValidationAvailableForEmployees !==
      defaultFormValues?.ibanComBicValidationAvailableForEmployees
    ) {
      updatedSettings.push({
        ibanComBicValidationAvailableForEmployees:
          values.ibanComBicValidationAvailableForEmployees as boolean,
        key: TransactionsSettingsKeys.IBAN_COM_BIC_VALIDATION_AVAILABLE_FOR_EMPLOYEES,
      });
    }
    // }

    // else if (provider === 'none') {
    //   updatedSettings = [
    //     {
    //       ibanComIbanValidationAvailableForCustomers: false,
    //       key: TransactionsSettingsKeys.IBAN_COM_IBAN_VALIDATION_AVAILABLE_FOR_CUSTOMERS,
    //     },
    //     {
    //       ibanComBicValidationAvailableForCustomers: false,
    //       key: TransactionsSettingsKeys.IBAN_COM_BIC_VALIDATION_AVAILABLE_FOR_CUSTOMERS,
    //     },
    //     {
    //       enableBicDataFromExternalSourceEdit: false,
    //       key: TransactionsSettingsKeys.ENABLE_BIC_DATA_FROM_EXTERNAL_SOURCE_EDIT,
    //     },
    //     {
    //       enableIbanDataFromExternalSourceEdit: false,
    //       key: TransactionsSettingsKeys.ENABLE_IBAN_DATA_FROM_EXTERNAL_SOURCE_EDIT,
    //     },
    //     {
    //       ibanComIbanValidationAvailableForEmployees: false,
    //       key: TransactionsSettingsKeys.IBAN_COM_IBAN_VALIDATION_AVAILABLE_FOR_EMPLOYEES,
    //     },
    //     {
    //       ibanComBicValidationAvailableForEmployees: false,
    //       key: TransactionsSettingsKeys.IBAN_COM_BIC_VALIDATION_AVAILABLE_FOR_EMPLOYEES,
    //     },
    //   ];
    // }

    if (updatedSettings.length > 0) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      }

      const body: IbanValidationFormValues = {
        ibanComIbanValidationAvailableForCustomers:
          values.ibanComIbanValidationAvailableForCustomers,
        ibanComBicValidationAvailableForCustomers:
          values.ibanComBicValidationAvailableForCustomers,
        enableBicDataFromExternalSourceEdit:
          values.enableBicDataFromExternalSourceEdit,
        enableIbanDataFromExternalSourceEdit:
          values.enableIbanDataFromExternalSourceEdit,
        ibanComIbanValidationAvailableForEmployees:
          values.ibanComIbanValidationAvailableForEmployees,
        ibanComBicValidationAvailableForEmployees:
          values.ibanComBicValidationAvailableForEmployees,
      };

      setDefaultFormValues(body);
    }
  });

  const getIbanSettingsAction = useCallback(async () => {
    const response = (await getIbanSettings()) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  useEffect(() => {
    if (!pspDetails) {
      getIbanSettingsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (pspDetails && !defaultFormValues) {
      const ibanSettings: PSPDetails[] = [];

      pspDetails?.forEach((item) => {
        if (
          item.key ===
            TransactionsSettingsKeys.IBAN_COM_IBAN_VALIDATION_AVAILABLE_FOR_CUSTOMERS ||
          item.key ===
            TransactionsSettingsKeys.IBAN_COM_BIC_VALIDATION_AVAILABLE_FOR_CUSTOMERS ||
          item.key ===
            TransactionsSettingsKeys.ENABLE_BIC_DATA_FROM_EXTERNAL_SOURCE_EDIT ||
          item.key ===
            TransactionsSettingsKeys.ENABLE_IBAN_DATA_FROM_EXTERNAL_SOURCE_EDIT ||
          item.key ===
            TransactionsSettingsKeys.IBAN_COM_IBAN_VALIDATION_AVAILABLE_FOR_EMPLOYEES ||
          item.key ===
            TransactionsSettingsKeys.IBAN_COM_BIC_VALIDATION_AVAILABLE_FOR_EMPLOYEES
        ) {
          ibanSettings.push(item);
        }
      });

      if (ibanSettings?.length) {
        const ibanComIbanValidationAvailableForCustomers = ibanSettings?.find(
          (item) =>
            item.key ===
            TransactionsSettingsKeys.IBAN_COM_IBAN_VALIDATION_AVAILABLE_FOR_CUSTOMERS,
        )?.data?.value as boolean;
        const ibanComBicValidationAvailableForCustomers = ibanSettings?.find(
          (item) =>
            item.key ===
            TransactionsSettingsKeys.IBAN_COM_BIC_VALIDATION_AVAILABLE_FOR_CUSTOMERS,
        )?.data?.value as boolean;
        const enableBicDataFromExternalSourceEdit = ibanSettings?.find(
          (item) =>
            item.key ===
            TransactionsSettingsKeys.ENABLE_BIC_DATA_FROM_EXTERNAL_SOURCE_EDIT,
        )?.data?.value as boolean;
        const enableIbanDataFromExternalSourceEdit = ibanSettings?.find(
          (item) =>
            item.key ===
            TransactionsSettingsKeys.ENABLE_IBAN_DATA_FROM_EXTERNAL_SOURCE_EDIT,
        )?.data?.value as boolean;
        const ibanComIbanValidationAvailableForEmployees = ibanSettings?.find(
          (item) =>
            item.key ===
            TransactionsSettingsKeys.IBAN_COM_IBAN_VALIDATION_AVAILABLE_FOR_EMPLOYEES,
        )?.data?.value as boolean;
        const ibanComBicValidationAvailableForEmployees = ibanSettings?.find(
          (item) =>
            item.key ===
            TransactionsSettingsKeys.IBAN_COM_BIC_VALIDATION_AVAILABLE_FOR_EMPLOYEES,
        )?.data?.value as boolean;

        const body: IbanValidationFormValues = {
          ibanComIbanValidationAvailableForCustomers,
          ibanComBicValidationAvailableForCustomers,
          enableBicDataFromExternalSourceEdit,
          enableIbanDataFromExternalSourceEdit,
          ibanComIbanValidationAvailableForEmployees,
          ibanComBicValidationAvailableForEmployees,
        };

        if (
          ibanComIbanValidationAvailableForCustomers ||
          ibanComBicValidationAvailableForCustomers ||
          enableBicDataFromExternalSourceEdit ||
          enableIbanDataFromExternalSourceEdit ||
          ibanComIbanValidationAvailableForEmployees ||
          ibanComBicValidationAvailableForEmployees
        ) {
          setProvider('iban.com');
        }

        reset(body);
        setDefaultFormValues(body);
      }
    }
  }, [pspDetails, defaultFormValues]);

  useEffect(() => {
    let title = 'Transaction Settings: IBAN/SWIFT Validation';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (isLoading || isFetching) {
    content = (
      <div className={cn.beneficiariesSettingsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={cn.beneficiariesSettingsContent}>
        {!errorList && (
          <>
            <div className={cn.selectProviderWrapper}>
              <Select
                name="provider"
                title="Provider"
                placeholder="Select provider"
                value={provider}
                list={providerList}
                onChange={(e) => {
                  setProvider(e.target.value as string);

                  if (e.target.value === 'none' && provider !== 'none') {
                    reset({
                      ibanComIbanValidationAvailableForCustomers: false,
                      ibanComBicValidationAvailableForCustomers: false,
                      enableBicDataFromExternalSourceEdit: false,
                      enableIbanDataFromExternalSourceEdit: false,
                      ibanComIbanValidationAvailableForEmployees: false,
                      ibanComBicValidationAvailableForEmployees: false,
                    });
                  } else if (e.target.value !== 'none' && provider === 'none') {
                    reset(defaultFormValues);
                  }
                }}
                fullWidth
              />
            </div>
            {provider !== 'none' && (
              <>
                <div className={cn.ibanValidationSettingBlock}>
                  <div className={cn.ibanValidationSettingBlockTitle}>
                    Online Bank
                  </div>
                  <div className={cn.ibanValidationSettingBlockContent}>
                    <div className={cn.ibanValidationSettingBlockGroup}>
                      <Controller
                        control={control}
                        name="ibanComIbanValidationAvailableForCustomers"
                        render={({ field: { value, name, onChange } }) => (
                          <>
                            <ToggleInput
                              name={name}
                              title="IBAN Validation"
                              onChange={(e) => {
                                onChange(e.target.checked);

                                if (
                                  !e.target.checked &&
                                  fields.enableIbanDataFromExternalSourceEdit
                                ) {
                                  setValue(
                                    'enableIbanDataFromExternalSourceEdit',
                                    false,
                                  );
                                }
                              }}
                              checked={value}
                            />
                            {errors?.ibanComIbanValidationAvailableForCustomers
                              ?.message && (
                              <div className={cn.settingError}>
                                {
                                  errors
                                    ?.ibanComIbanValidationAvailableForCustomers
                                    ?.message
                                }
                              </div>
                            )}
                          </>
                        )}
                      />
                      {fields.ibanComIbanValidationAvailableForCustomers && (
                        <div className={cn.hiddenSetting}>
                          <Controller
                            control={control}
                            name="enableIbanDataFromExternalSourceEdit"
                            render={({ field: { value, name, onChange } }) => (
                              <>
                                <ToggleInput
                                  name={name}
                                  title="Editing data from an external source"
                                  onChange={(e) => onChange(e.target.checked)}
                                  checked={value}
                                />
                                {errors?.enableIbanDataFromExternalSourceEdit
                                  ?.message && (
                                  <div className={cn.settingError}>
                                    {
                                      errors
                                        ?.enableIbanDataFromExternalSourceEdit
                                        ?.message
                                    }
                                  </div>
                                )}
                              </>
                            )}
                          />
                        </div>
                      )}
                    </div>
                    <div className={cn.ibanValidationSettingBlockGroup}>
                      <Controller
                        control={control}
                        name="ibanComBicValidationAvailableForCustomers"
                        render={({ field: { value, name, onChange } }) => (
                          <>
                            <ToggleInput
                              name={name}
                              title="BIC Validation"
                              onChange={(e) => {
                                onChange(e.target.checked);

                                if (
                                  !e.target.checked &&
                                  fields.enableBicDataFromExternalSourceEdit
                                ) {
                                  setValue(
                                    'enableBicDataFromExternalSourceEdit',
                                    false,
                                  );
                                }
                              }}
                              checked={value}
                            />
                            {errors?.ibanComBicValidationAvailableForCustomers
                              ?.message && (
                              <div className={cn.settingError}>
                                {
                                  errors
                                    ?.ibanComBicValidationAvailableForCustomers
                                    ?.message
                                }
                              </div>
                            )}
                          </>
                        )}
                      />
                      {fields.ibanComBicValidationAvailableForCustomers && (
                        <div className={cn.hiddenSetting}>
                          <Controller
                            control={control}
                            name="enableBicDataFromExternalSourceEdit"
                            render={({ field: { value, name, onChange } }) => (
                              <>
                                <ToggleInput
                                  name={name}
                                  title="Editing data from an external source"
                                  onChange={(e) => onChange(e.target.checked)}
                                  checked={value}
                                />
                                {errors?.enableBicDataFromExternalSourceEdit
                                  ?.message && (
                                  <div className={cn.settingError}>
                                    {
                                      errors
                                        ?.enableBicDataFromExternalSourceEdit
                                        ?.message
                                    }
                                  </div>
                                )}
                              </>
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={cn.ibanValidationSettingBlock}>
                  <div className={cn.ibanValidationSettingBlockTitle}>
                    Admin Panel
                  </div>
                  <div className={cn.ibanValidationSettingBlockContent}>
                    <div className={cn.ibanValidationSettingBlockGroup}>
                      <Controller
                        control={control}
                        name="ibanComIbanValidationAvailableForEmployees"
                        render={({ field: { value, name, onChange } }) => (
                          <>
                            <ToggleInput
                              name={name}
                              title="IBAN Validation"
                              onChange={(e) => onChange(e.target.checked)}
                              checked={value}
                            />
                            {errors?.ibanComIbanValidationAvailableForEmployees
                              ?.message && (
                              <div className={cn.settingError}>
                                {
                                  errors
                                    ?.ibanComIbanValidationAvailableForEmployees
                                    ?.message
                                }
                              </div>
                            )}
                          </>
                        )}
                      />
                    </div>
                    <div className={cn.ibanValidationSettingBlockGroup}>
                      <Controller
                        control={control}
                        name="ibanComBicValidationAvailableForEmployees"
                        render={({ field: { value, name, onChange } }) => (
                          <>
                            <ToggleInput
                              name={name}
                              title="BIC Validation"
                              onChange={(e) => onChange(e.target.checked)}
                              checked={value}
                            />
                            {errors?.ibanComBicValidationAvailableForEmployees
                              ?.message && (
                              <div className={cn.settingError}>
                                {
                                  errors
                                    ?.ibanComBicValidationAvailableForEmployees
                                    ?.message
                                }
                              </div>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {globalError && (
              <div className={cn.saveGlobalError}>{globalError}</div>
            )}
            <ButtonPrimary
              text={editIsLoading ? 'Loading...' : 'Save'}
              className={cn.saveBeneficiariesSettings}
              disabled={!isDirty || editIsLoading}
              onClick={onSubmit}
              bold
            />
          </>
        )}
        {errorList && (
          <div className={cn.loadingError}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errorList}</div>
          </div>
        )}
      </div>
    );
  }

  return <div className={cn.beneficiariesSettingsWrapper}>{content}</div>;
};

export default IbanValidationSettings;
